import { useState, useCallback, useMemo } from 'react';

/**
 * Panel names as constants for type-safe references.
 */
export const PANEL_NAMES = {
  MEASUREMENT: 'measurement',
  GAUSSIAN: 'gaussian',
  MODELS_3D: 'models3D',
  VIEWPOINTS: 'viewpoints',
  SETTINGS: 'settings',
  BASE_LAYERS: 'baseLayers',
  CLIPPING_PLANE: 'clippingPlane',
  PROFILER: 'profiler',
  ANNOTATIONS: 'annotations',
  TRANSFORMATION: 'transformation',
};

const ALL_PANELS = Object.values(PANEL_NAMES);

const INITIAL_PANELS = Object.fromEntries(ALL_PANELS.map(name => [name, false]));

// Callbacks to fire when specific panels close
const CLOSE_CALLBACKS = {
  [PANEL_NAMES.SETTINGS]: 'onSettingsClose',
  [PANEL_NAMES.MEASUREMENT]: 'onMeasurementClose',
};

/**
 * Hook to manage panel toggle states with a single state object.
 * Only one panel can be open at a time.
 */
export function useViewerPanels({ onSettingsClose, onMeasurementClose, setMode, modeNone } = {}) {
  const [panels, setPanels] = useState(INITIAL_PANELS);
  const [profileLoadingId, setProfileLoadingId] = useState(null);

  const callbacks = useMemo(() => ({
    onSettingsClose,
    onMeasurementClose,
  }), [onSettingsClose, onMeasurementClose]);

  /**
   * Close all panels and reset measurement mode.
   */
  const closeAllPanels = useCallback(() => {
    setPanels(INITIAL_PANELS);
    if (setMode && modeNone) {
      setMode(modeNone);
    }
  }, [setMode, modeNone]);

  /**
   * Open a specific panel, closing all others.
   */
  const openPanel = useCallback((panelName) => {
    if (!ALL_PANELS.includes(panelName)) {
      console.warn(`Unknown panel name: ${panelName}`);
      return;
    }
    setPanels({ ...INITIAL_PANELS, [panelName]: true });
    if (setMode && modeNone) {
      setMode(modeNone);
    }
  }, [setMode, modeNone]);

  /**
   * Close a specific panel with optional callbacks.
   */
  const closePanel = useCallback((panelName) => {
    if (!ALL_PANELS.includes(panelName)) {
      console.warn(`Unknown panel name: ${panelName}`);
      return;
    }
    setPanels(prev => ({ ...prev, [panelName]: false }));
    const callbackKey = CLOSE_CALLBACKS[panelName];
    if (callbackKey) callbacks[callbackKey]?.();
  }, [callbacks]);

  /**
   * Toggle a specific panel. If opening, closes all others first.
   */
  const togglePanel = useCallback((panelName) => {
    setPanels(prev => {
      if (prev[panelName]) {
        // Closing — fire close callback
        const callbackKey = CLOSE_CALLBACKS[panelName];
        if (callbackKey) callbacks[callbackKey]?.();
        return { ...prev, [panelName]: false };
      }
      // Opening — close all others, reset mode
      if (setMode && modeNone) setMode(modeNone);
      return { ...INITIAL_PANELS, [panelName]: true };
    });
  }, [callbacks, setMode, modeNone]);

  /**
   * Check if a specific panel is open.
   */
  const isPanelOpen = useCallback((panelName) => {
    return panels[panelName] || false;
  }, [panels]);

  /**
   * Close point-cloud-specific panels.
   */
  const closePointCloudPanels = useCallback(() => {
    setPanels(prev => ({
      ...prev,
      [PANEL_NAMES.MEASUREMENT]: false,
      [PANEL_NAMES.GAUSSIAN]: false,
      [PANEL_NAMES.CLIPPING_PLANE]: false,
      [PANEL_NAMES.PROFILER]: false,
    }));
  }, []);

  // Generic setter factory for individual panel setters
  const makeSetter = useCallback((panelName) => (value) => {
    setPanels(prev => ({ ...prev, [panelName]: value }));
  }, []);

  // Individual setters (for backward compatibility)
  const setMeasurementPanelOpen = useMemo(() => makeSetter(PANEL_NAMES.MEASUREMENT), [makeSetter]);
  const setGaussianPanelOpen = useMemo(() => makeSetter(PANEL_NAMES.GAUSSIAN), [makeSetter]);
  const setModelsPanel3DOpen = useMemo(() => makeSetter(PANEL_NAMES.MODELS_3D), [makeSetter]);
  const setViewpointsPanelOpen = useMemo(() => makeSetter(PANEL_NAMES.VIEWPOINTS), [makeSetter]);
  const setSettingsPanelOpen = useMemo(() => makeSetter(PANEL_NAMES.SETTINGS), [makeSetter]);
  const setBaseLayerPanelOpen = useMemo(() => makeSetter(PANEL_NAMES.BASE_LAYERS), [makeSetter]);
  const setClippingPlanePanelOpen = useMemo(() => makeSetter(PANEL_NAMES.CLIPPING_PLANE), [makeSetter]);
  const setProfilerPanelOpen = useMemo(() => makeSetter(PANEL_NAMES.PROFILER), [makeSetter]);
  const setAnnotationsPanelOpen = useMemo(() => makeSetter(PANEL_NAMES.ANNOTATIONS), [makeSetter]);
  const setTransformationPanelOpen = useMemo(() => makeSetter(PANEL_NAMES.TRANSFORMATION), [makeSetter]);

  // Individual toggle functions (for backward compatibility)
  const toggleMeasurementPanel = useMemo(() => () => togglePanel(PANEL_NAMES.MEASUREMENT), [togglePanel]);
  const toggleGaussianPanel = useMemo(() => () => togglePanel(PANEL_NAMES.GAUSSIAN), [togglePanel]);
  const toggle3DModelsPanel = useMemo(() => () => togglePanel(PANEL_NAMES.MODELS_3D), [togglePanel]);
  const toggleViewpointsPanel = useMemo(() => () => togglePanel(PANEL_NAMES.VIEWPOINTS), [togglePanel]);
  const toggleSettingsPanel = useMemo(() => () => togglePanel(PANEL_NAMES.SETTINGS), [togglePanel]);
  const toggleBaseLayerPanel = useMemo(() => () => togglePanel(PANEL_NAMES.BASE_LAYERS), [togglePanel]);
  const toggleClippingPlanePanel = useMemo(() => () => togglePanel(PANEL_NAMES.CLIPPING_PLANE), [togglePanel]);
  const toggleProfilerPanel = useMemo(() => () => togglePanel(PANEL_NAMES.PROFILER), [togglePanel]);
  const toggleAnnotationsPanel = useMemo(() => () => togglePanel(PANEL_NAMES.ANNOTATIONS), [togglePanel]);
  const toggleTransformationPanel = useMemo(() => () => togglePanel(PANEL_NAMES.TRANSFORMATION), [togglePanel]);

  return {
    // Individual panel states (for backward compatibility)
    measurementPanelOpen: panels[PANEL_NAMES.MEASUREMENT],
    gaussianPanelOpen: panels[PANEL_NAMES.GAUSSIAN],
    modelsPanel3DOpen: panels[PANEL_NAMES.MODELS_3D],
    viewpointsPanelOpen: panels[PANEL_NAMES.VIEWPOINTS],
    settingsPanelOpen: panels[PANEL_NAMES.SETTINGS],
    baseLayerPanelOpen: panels[PANEL_NAMES.BASE_LAYERS],
    clippingPlanePanelOpen: panels[PANEL_NAMES.CLIPPING_PLANE],
    profilerPanelOpen: panels[PANEL_NAMES.PROFILER],
    annotationsPanelOpen: panels[PANEL_NAMES.ANNOTATIONS],
    transformationPanelOpen: panels[PANEL_NAMES.TRANSFORMATION],

    // Profile loading state
    profileLoadingId,
    setProfileLoadingId,

    // Panel state setters (for direct access if needed)
    setMeasurementPanelOpen,
    setGaussianPanelOpen,
    setModelsPanel3DOpen,
    setViewpointsPanelOpen,
    setSettingsPanelOpen,
    setBaseLayerPanelOpen,
    setClippingPlanePanelOpen,
    setProfilerPanelOpen,
    setAnnotationsPanelOpen,
    setTransformationPanelOpen,

    // Management methods
    openPanel,
    closePanel,
    togglePanel,
    isPanelOpen,
    closeAllPanels,
    closePointCloudPanels,

    // Individual toggle functions (for backward compatibility)
    toggleMeasurementPanel,
    toggleGaussianPanel,
    toggle3DModelsPanel,
    toggleViewpointsPanel,
    toggleSettingsPanel,
    toggleBaseLayerPanel,
    toggleClippingPlanePanel,
    toggleProfilerPanel,
    toggleAnnotationsPanel,
    toggleTransformationPanel,
  };
}

export default useViewerPanels;
